(() => {
  const SECTION_SELECTOR = '[data-collection-section]';

  /* ------------------------------------------------------------------ */
  /* Card — accordion toggle + qty stepper + AJAX add-to-cart            */
  /* ------------------------------------------------------------------ */

  function initCard(card) {
    if (card.dataset.collectionCardInit) return;
    card.dataset.collectionCardInit = 'true';

    const trigger = card.querySelector('[data-collection-trigger]');
    const panel   = card.querySelector('[data-collection-panel]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isOpen));
      if (isOpen) {
        panel.setAttribute('hidden', '');
        card.classList.remove('is-open');
      } else {
        panel.removeAttribute('hidden');
        card.classList.add('is-open');
      }
    });

    /* Qty steppers inside each variant row */
    panel.querySelectorAll('[data-collection-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('[data-qty-input]');
      stepper.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) || 1;
        if (val > 1) input.value = val - 1;
      });
      stepper.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) || 1;
        input.value = val + 1;
      });
      input.addEventListener('change', () => {
        const val = parseInt(input.value, 10);
        if (isNaN(val) || val < 1) input.value = 1;
      });
    });

    /* AJAX add-to-cart per variant */
    panel.querySelectorAll('[data-collection-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const variantId = btn.dataset.variantId;
        if (!variantId || btn.disabled) return;

        const row = btn.closest('.collection-card__row-actions');
        const input = row?.querySelector('[data-qty-input]');
        const qty = input ? (parseInt(input.value, 10) || 1) : 1;

        btn.disabled = true;
        btn.dataset.state = 'adding';
        const originalLabel = btn.textContent;
        btn.textContent = '…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: qty }),
        })
          .then((res) => {
            if (!res.ok) throw new Error(res.statusText);
            return res.json();
          })
          .then(() => {
            delete btn.dataset.state;
            btn.textContent = btn.dataset.addedLabel || 'Added!';
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            setTimeout(() => {
              btn.textContent = btn.dataset.addLabel || originalLabel;
              btn.disabled = false;
            }, 1600);
          })
          .catch(() => {
            delete btn.dataset.state;
            btn.textContent = btn.dataset.addLabel || originalLabel;
            btn.disabled = false;
          });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Load more — appends the next paginated product batch inline          */
  /* ------------------------------------------------------------------ */

  function initLoadMore(section) {
    const loadMore = section.querySelector('[data-collection-load-more]');
    const list = section.querySelector('.collection-section__list');
    if (!loadMore || !list || loadMore.dataset.collectionLoadMoreInit) return;

    const link = loadMore.querySelector('[data-load-more-link]');
    if (!link) return;

    loadMore.dataset.collectionLoadMoreInit = 'true';

    link.addEventListener('click', (event) => {
      event.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;

      link.setAttribute('aria-busy', 'true');

      fetch(link.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nextSection = doc.querySelector(SECTION_SELECTOR);
          const nextCards = nextSection?.querySelectorAll('[data-collection-card]');
          const nextLoadMore = nextSection?.querySelector('[data-collection-load-more]');

          if (!nextCards?.length) {
            window.location.href = link.href;
            return;
          }

          nextCards.forEach((card) => {
            const importedCard = document.importNode(card, true);
            list.appendChild(importedCard);
            initCard(importedCard);
          });

          if (nextLoadMore) {
            loadMore.innerHTML = nextLoadMore.innerHTML;
            loadMore.dataset.visibleCount = nextLoadMore.dataset.visibleCount || '';
            loadMore.dataset.totalCount = nextLoadMore.dataset.totalCount || '';
            delete loadMore.dataset.collectionLoadMoreInit;
            initLoadMore(section);
          } else {
            loadMore.remove();
          }
        })
        .catch(() => {
          window.location.href = link.href;
        })
        .finally(() => {
          link.removeAttribute('aria-busy');
        });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function initSection(section) {
    if (section.dataset.collectionInit) return;
    section.dataset.collectionInit = 'true';
    section.querySelectorAll('[data-collection-card]').forEach(initCard);
    initLoadMore(section);
    initTabsScroll(section);
  }

  /* ------------------------------------------------------------------ */
  /* Tabs — scroll fade indicator                                        */
  /* ------------------------------------------------------------------ */

  function initTabsScroll(section) {
    const wrapper = section.querySelector('[data-tabs-wrapper]');
    const scroller = section.querySelector('[data-tabs-scroll]');
    if (!wrapper || !scroller) return;

    function updateFade() {
      const atEnd = scroller.scrollLeft + scroller.offsetWidth >= scroller.scrollWidth - 4;
      const canScroll = scroller.scrollWidth > scroller.offsetWidth + 4;
      wrapper.classList.toggle('is-at-end', atEnd || !canScroll);
    }

    scroller.addEventListener('scroll', updateFade, { passive: true });
    window.addEventListener('resize', updateFade);
    updateFade();
  }

  function initAll(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });
})();
